import React, { useState, useEffect, useRef } from "react";
import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  IconButton as MuiIconButton,
  Typography,
} from "@mui/material";
import { Box, styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import { DownloadIcon } from "@/icons";
import { useLazyDocumentDownloadQuery } from "@/api/services/profile/profile_/OtherDocumentsApi";
import Loader from "../loader";

// Move styled components outside the functional component
const Dialog = styled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    overflowX: "hidden",
    fontFamily: "Lato, sans-serif",
  },
}));

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: "18px !important",
  lineHeight: "21.6px",
}));

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  position: "absolute",
  right: 10,
  top: 8,
  color: "white",
}));

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  overflowX: "hidden",
  overflowY: "auto",
  padding: theme.spacing(2),
  fontFamily: "Lato, sans-serif",
}));

const DocViewModal = ({
  folder = "",
  file = [],
  open = false,
  setOpen = () => {},
  width = "520px",
  moduleName = "",
  apiPath = "",
}) => {
  const [blobToPdf, setBlobToPdf] = useState(null);
  const [imageUrls, setImageUrls] = useState("");
  const [isLoaded, setIsLoaded] = useState(true);
  const loginDetails = JSON.parse(localStorage.getItem("loginDetails"));
  const employeeRole = loginDetails?.userDetails?.roleType;
  const [documentDownload] = useLazyDocumentDownloadQuery();
  const downloadLinkRef = useRef(null);

  const handleClose = () => {
    setOpen(false);
    setIsLoaded(false);
  };

  const token = JSON.parse(localStorage.getItem("loginDetails")).token;

  const handleFileApi = async (folder, fileName, fileType) => {
    try {
      setIsLoaded(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_DEV_IMG_URL}${apiPath}${folder}/${fileName}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`Failed to fetch ${fileType}`);

      const blob = await response.blob();

      if (fileType === "pdf") {
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setBlobToPdf(`${blobUrl}#toolbar-0`);
      } else if (fileType === "image") {
        const imageUrl = URL.createObjectURL(blob);
        setImageUrls(imageUrl);
      }
      
    } catch (error) {
      console.error(`Error fetching ${fileType}:`, error);
      setIsLoaded(false);
    } finally{
      setIsLoaded(false);
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    console.log(file, "file");
    let fileNameValue = "";
    if (Array.isArray(file)) {
      fileNameValue = file[0]; // Take first element from array
    } else if (typeof file === "string") {
      fileNameValue = file; // Directly the filename
    } else {
      // console.error("Invalid file format:", file);
      // return;
    }
    console.log("fileNameValue", fileNameValue);

    try {
      const response = await documentDownload({
        folder,
        file: fileNameValue,
      }).unwrap();

      const url = window.URL.createObjectURL(response); // response is already a Blob
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileNameValue);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (file && Array.isArray(file)) {
      file.forEach((item) => {
        const fileExtension = item.split(".").pop().toLowerCase();
        if (fileExtension === "pdf") {
          handleFileApi(folder, item, "pdf");
        } else if (
          ["jpg", "jpeg", "png", "gif", "bmp"].includes(fileExtension)
        ) {
          handleFileApi(folder, item, "image");
        }
      });
    } else {
      const fileExtension = file.split(".").pop().toLowerCase();
      if (fileExtension === "pdf") {
        handleFileApi(folder, file, "pdf");
      } else if (["jpg", "jpeg", "png", "gif", "bmp"].includes(fileExtension)) {
        handleFileApi(folder, file, "image");
      }
    }
  }, [file, folder, open]);

  return (
    <>
      <a ref={downloadLinkRef} style={{ display: "none" }} />
      {/* {isLoaded && ( */}
        <Dialog
          fullWidth
          scroll="body"
          open={open}
          onClose={handleClose}
          maxWidth={width || "lg"}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{folder || "File Preview"}</span>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {file &&
                  String(file).toLowerCase().endsWith(".pdf") === false &&
                  employeeRole === "HRSS" &&
                  moduleName === "Other Documents" && (
                    <IconButton
                      edge="start"
                      color="primary"
                      onClick={handleDownload}
                      aria-label="download"
                      sx={{ mr: 2 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  )}

                <IconButton
                  edge="end"
                  color="primary"
                  onClick={handleClose}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            
            
            {isLoaded ?(<Loader/>): file && file.length > 0 ?(file?.map((item, index) => {
              const fileExtension = item.split(".").pop().toLowerCase();

              return fileExtension === "pdf" ? (
                <iframe
                  key={index}
                  src={blobToPdf}
                  width="100%"
                  height="550px"
                  title={`PDF Viewer ${index}`}
                  style={{ border: "none" }}
                />
              ) : (
                <Box
                  sx={{
                    pt: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    overflow: "hidden",
                  }}
                  key={index}
                >
                  <img
                    src={imageUrls}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "500px",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              );
            })):(<Typography>No File Found</Typography>)}
          </DialogContent>
        </Dialog>
      {/* )} */}
    </>
  );
};

export default DocViewModal;
